import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  FormControlLabel,
  FormHelperText,
  LinearProgress,
  Switch,
  useMediaQuery,
} from '@mui/material';

import { Modal } from '../../../../shared/ui/Modal';
import { TextInput } from '../../../../shared/ui/TextInput';
import {
  Footer,
  FormBody,
  FormWrapper,
  Grid,
  Header,
  Section,
  SectionLabel,
  SectionTitle,
} from '../../../../shared/ui/Modal/Body';
import { PrimaryButton } from '../../../../shared/ui/Inputs';
import { ProductBasket } from '../../../../types/basket/ProductBasket';
import { TableProducts } from './products/tableProducts';
import { SelectProduct } from './products/selectProduct';
import { CreateBasketPayload } from '../../../../datasources/baskets/BasketsDataSource';
import { Basket } from '../../../../types/basket/Basket';

export type DialogBasketProps = {
  open: boolean;
  mode: 'create' | 'edit';
  onClose: () => void;
  loading?: boolean;
  error?: string;
  progress?: number;
  canConfirm?: boolean;
  onConfirm?: (payload: CreateBasketPayload) => void | Promise<void>;
  initialBasket?: Basket;
};

type BasketField = 'code' | 'name' | 'allow_model_selector' | 'products';

type BasketForm = {
  code: string;
  name: string;
};

type BasketFieldErrors = Partial<Record<BasketField, string>>;

type TouchedFields = Partial<Record<BasketField, boolean>>;

const fieldLabels: Record<BasketField, string> = {
  code: 'Código',
  name: 'Nome',
  allow_model_selector: 'Especificar modelo',
  products: 'Produtos',
};

const fieldErrorMessages = {
  required: 'Este campo é obrigatório.',
  code_syntax_error: 'O código inserido é inválido.',
  name_syntax_error: 'O nome inserido é inválido.',
} as const;

const initialForm: BasketForm = {
  code: '',
  name: '',
};

export const DialogBasket: React.FC<DialogBasketProps> = ({
  open,
  mode,
  onClose,
  loading = false,
  error,
  progress,
  canConfirm = true,
  onConfirm,
  initialBasket,
}) => {
  const [form, setForm] = useState<BasketForm>({
    ...initialForm,
  });

  const [fieldErrors, setFieldErrors] = useState<BasketFieldErrors>({});
  const [touchedFields, setTouchedFields] = useState<TouchedFields>({});
  const [selectedProducts, setSelectedProducts] = useState<ProductBasket[]>([]);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [onAllowModel, setOnAllowModel] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm({
      code: initialBasket?.code ?? '',
      name: initialBasket?.name ?? '',
    });

    setFieldErrors({});
    setTouchedFields({});
    setSelectedProducts(
      initialBasket?.basket_items
        .filter((item) => item.product)
        .map((item) => ({
          id: item.product.id,
          sap_code: item.product_sap_code || item.product.sap_code,
          description: item.product.description,
        })) ?? []
    );
    setProductPickerOpen(false);
    setOnAllowModel(initialBasket?.allow_model_selector ?? false);
  }, [open, initialBasket]);

  const disabled = loading;

  function validateField(
    field: BasketField,
    value: string
  ): string | undefined {
    const normalizedValue = value.trim();

    if (field === 'name') {
      if (!normalizedValue) {
        return fieldErrorMessages.required;
      }

      if (normalizedValue.length < 3) {
        return 'O nome deve possuir pelo menos 3 caracteres.';
      }

      if (normalizedValue.length > 100) {
        return 'O nome deve possuir no máximo 100 caracteres.';
      }

      return undefined;
    }

    if (field === 'code') {
      if (!normalizedValue) {
        return fieldErrorMessages.required;
      }

      const codePattern = /^[a-zA-Z0-9_-]+$/;

      if (!codePattern.test(normalizedValue)) {
        return fieldErrorMessages.code_syntax_error;
      }

      return undefined;
    }

    return undefined;
  }

  function handleFieldChange(field: keyof BasketForm, value: string): void {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));

    /*
     * Se o usuário já saiu do campo uma vez, revalidamos enquanto ele digita.
     * Isso remove a mensagem assim que o valor se torna válido.
     */
    if (touchedFields[field]) {
      setFieldErrors((currentErrors) => ({
        ...currentErrors,
        [field]: validateField(field, value),
      }));
    }
  }

  function touchField(field: keyof BasketForm): void {
    setTouchedFields((currentTouched) => ({
      ...currentTouched,
      [field]: true,
    }));

    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      [field]: validateField(field, form[field]),
    }));
  }

  function getFieldError(field: BasketField): string | undefined {
    if (!touchedFields[field]) {
      return undefined;
    }

    return fieldErrors[field];
  }

  function validateForm(): boolean {
    const nextErrors: BasketFieldErrors = {
      code: validateField('code', form.code),
      name: validateField('name', form.name),
    };

    setTouchedFields({
      code: true,
      name: true,
    });

    setFieldErrors(nextErrors);

    return !Object.values(nextErrors).some(Boolean);
  }

  async function handleConfirm(): Promise<void> {
    const isValid = validateForm();

    if (!isValid || selectedProducts.length === 0) {
      return;
    }

    await onConfirm?.({
      code: form.code.trim(),
      name: form.name.trim(),
      products: selectedProducts,
      allowModelSelector: onAllowModel,
    });
  }

  const isFormValid = useMemo(() => {
    return (
      !validateField('code', form.code) &&
      !validateField('name', form.name) &&
      selectedProducts.length > 0
    );
  }, [form.code, form.name, selectedProducts.length]);

  const confirmDisabled = loading || disabled || !canConfirm || !isFormValid;

  const gridCols = useMediaQuery('(max-width: 1224px)') ? 1 : 2;

  return (
    <Modal open={open} width="min(640px, 95vw)" gap={10}>
      <Header>
        <span
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: '#444',
          }}
        >
          {mode === 'edit' ? 'Editar Balaio' : 'Novo Balaio'}
        </span>
      </Header>
      <FormBody>
        <SectionTitle>Dados do Balaio</SectionTitle>
        <FormWrapper>
          <Section>
            <SectionLabel>Identificação</SectionLabel>
            <Grid cols={gridCols}></Grid>
            <TextInput
              value={form.name}
              label={fieldLabels.name}
              handleChange={(event) =>
                handleFieldChange('name', event.target.value)
              }
              onBlur={() => touchField('name')}
              disabled={disabled}
              error={Boolean(getFieldError('name'))}
              helperText={getFieldError('name')}
            />

            <TextInput
              value={form.code}
              label={fieldLabels.code}
              handleChange={(event) =>
                handleFieldChange('code', event.target.value)
              }
              onBlur={() => touchField('code')}
              disabled={disabled}
              error={Boolean(getFieldError('code'))}
              helperText={getFieldError('code')}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={onAllowModel}
                  onChange={(e) => setOnAllowModel(e.target.checked)}
                />
              }
              label="Especificar modelo"
              labelPlacement="start"
            />
          </Section>
          <Section>
            <SectionLabel>Produtos</SectionLabel>
            <Grid cols={1}>
              <TableProducts
                products={selectedProducts}
                disabled={disabled}
                onChange={setSelectedProducts}
              />
              {selectedProducts.length === 0 && (
                <FormHelperText error>
                  Adicione pelo menos um produto.
                </FormHelperText>
              )}
              <PrimaryButton
                style={{
                  width: '100%',
                  paddingTop: '10px',
                  paddingBottom: '10px',
                  fontSize: '15px',
                  height: 'auto',
                }}
                disabled={disabled}
                onClick={() => {
                  setProductPickerOpen(true);
                }}
              >
                Adicionar Produto
              </PrimaryButton>
            </Grid>
          </Section>

          {loading && (
            <LinearProgress
              variant={
                typeof progress === 'number' ? 'determinate' : 'indeterminate'
              }
              value={progress}
            />
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </FormWrapper>
      </FormBody>
      <Footer>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={confirmDisabled}
        >
          {loading ? 'Salvando...' : 'Confirmar'}
        </Button>
      </Footer>
      <SelectProduct
        open={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        existingProducts={selectedProducts}
        onConfirm={(products) => {
          setSelectedProducts((current) => {
            const existingIds = new Set(current.map((product) => product.id));
            return [
              ...current,
              ...products.filter((product) => !existingIds.has(product.id)),
            ];
          });
        }}
      />
    </Modal>
  );
};
